import React, { Component } from "react"
import PropTypes from "prop-types"
import isEqual from "lodash/isEqual"
import Highcharts from "highcharts/highstock"
import Exporting from "highcharts/modules/exporting"
import { translate } from "react-i18next"
import { CHARTS } from "../../../constants"
import { getOrdinalSuffix, formatDecimalForDisplay } from "../../../utils/formatting-utils"

class BarChart extends Component {
  componentDidMount = () => {
    this.createChart()
  }

  shouldComponentUpdate = (nextProps) => {
    return (
      this.props.scrollbarEnabled !== nextProps.scrollbarEnabled ||
      !isEqual(this.props.data, nextProps.data)
    )
  }

  componentDidUpdate = () => {
    this.createChart()
  }

  componentWillUnmount = () => {
    this.chart.destroy()
  }

  createChart = () => {
    // Get the element into which to insert the chart
    const chartContainer = this.chartContainerRef

    const {
      t, xAxisTitle, yAxisTitle, scrollbarEnabled, tooltipFormatter, titleKey, subtitleKey
    } = this.props

    const fontSize = 11
    const seriesBars = [
      {
        name: t(xAxisTitle),
        data: this.props.data,
      },
    ]

    Exporting(Highcharts)

    const barCount = this.props.data.length
    const exportImageHeight = 40 * barCount

    // Create the chart
    this.chart = new Highcharts.Chart({
      chart: {
        renderTo: chartContainer,
        height: CHARTS.standardHeight - 28,
        type: "bar",
      },
      lang: {
        contextButtonTitle: t("Common.ChartDownloadOptions"),
      },
      title: {
        text: titleKey != null && titleKey.length > 0 ? t(titleKey) : null,
        y: -30
      },
      subtitle: {
        text: subtitleKey != null && subtitleKey.length > 0 ? t(subtitleKey) : null,
        y: -15
      },
      colors: CHARTS.getColours(this.props.data.length),

      xAxis: {
        allowDecimals: false,
        type: "category",
        title: {
          text: null,
        },
        scrollbar: {
          enabled: scrollbarEnabled,
          liveRedraw: false,
        },
        labels: {
          enabled: false,
        },
        gridLineWidth: 0,
        tickLength: 0,
        min: 0,
        max: 4,
      },
      yAxis: {
        title: {
          text: yAxisTitle ? t(yAxisTitle) : null,
        },
        gridLineDashStyle: "ShortDot",
      },
      plotOptions: {
        bar: {
          dataLabels: {
            enabled: true,
          },
        },
        series: {
          dataLabels: {
            enabled: true,
            formatter() {
              if (this.point) {
                const text = `${this.key}... ${this.y}%`
                // font size is in pixels, so if we divide the height of the bar by it
                // We should get amount of characters that can fit in the space
                const labelSpaceLength = this.point.shapeArgs.height / fontSize

                if (labelSpaceLength < text.length) {
                  return `${this.key.substring(0, labelSpaceLength)}... ${this.y}%`
                }
                return `${this.key} ${this.y}%`
              }
            },
            inside: true,
            style: {
              color: "#333",
              textOutline: 0,
              fontSize,
              fontWeight: "normal",
            },
            align: "left",
            crop: true,
            allowOverlap: false,
          },
          colorByPoint: true,
          groupPadding: 0,
          animation: CHARTS.animationEnabled,
          series: {
            minPointLength: 2,
          },
        },
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        formatter() {
          if (tooltipFormatter) {
            return tooltipFormatter(this)
          }

          return `<b>${getOrdinalSuffix(this.x + 1)}</b><br/><span style="color:${
            this.point.color
          }">\u25CF</span>${this.key}: ${formatDecimalForDisplay(this.point.y)}%`
        },
      },
      exporting: {
        enabled: CHARTS.exportEnabled,
        buttons: {
          contextButton: {
            menuItems: CHARTS.exportButtons,
            x: scrollbarEnabled ? -25 : 0,
          },
        },
        sourceHeight: exportImageHeight,
        filename: titleKey != null && titleKey.length > 0 ? t(titleKey) : null,
        chartOptions: {
          xAxis: {
            max: barCount - 1,
          },
          chart: {
            spacingTop: 50,
          },
        },
      },
      series: seriesBars,
    })
  }

  render() {
    return (
      <div
        className="chart-container col-md-12"
        ref={(c) => {
          this.chartContainerRef = c
        }}
      />
    )
  }
}

BarChart.propTypes = {
  data: PropTypes.array.isRequired,
  yAxisTitle: PropTypes.string.isRequired,
  xAxisTitle: PropTypes.string,
  scrollbarEnabled: PropTypes.bool.isRequired,
  tooltipFormatter: PropTypes.func,
  t: PropTypes.func.isRequired,
  titleKey: PropTypes.string,
  subtitleKey: PropTypes.string
}

BarChart.defaultProps = {
  tooltipFormatter: null,
  titleKey: "",
  subtitleKey: ""
}

export default translate()(BarChart)



// WEBPACK FOOTER //
// ./src/components/charts/generic/BarChart.jsx